import React, { useState } from "react";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import PorterTable from "components/PorterTable";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import VuiPaginationControls from "components/ui/VuiPagination/custom/VuiCustomPagination";
import { IoInformationCircleOutline } from "react-icons/io5"; // For tooltip icon
import Tooltip from "@mui/material/Tooltip";

// Import the custom hook
import useProductMentionsTableData from "../data/useProductMentionsTableData";

import { useAnalytics } from "context/AnalyticsContext";
import { ACCENT_COLORS } from "utils/designTokens";
import IconButton from "@mui/material/IconButton";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import Modal from "@mui/material/Modal";
import GlassCard from "components/GlassCard";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "1500px",
  height: "85vh", // Attempt to use more of the viewport height by default
  maxHeight: "90vh", // Still constrained by maxHeight
  bgcolor: "rgba(20, 20, 30, 0.95)",
  border: `2px solid ${ACCENT_COLORS.highlight}`,
  borderRadius: "12px",
  boxShadow: 24,
  p: 3, // Padding (theme.spacing(3) = 24px)
  display: "flex",
  flexDirection: "column",
};

function ProductMentionsTable() {

  const { selectedCompetitor, selectedPresetKey, competitors } = useAnalytics();
  // Modal (Expand table)
  const [isProductsTableModalOpen, setIsProductsTableModalOpen] = useState(false);
  const handleOpenProductsTableModal = () => setIsProductsTableModalOpen(true);
  const handleCloseProductsTableModal = () => setIsProductsTableModalOpen(false);

  const competitorDisplayName = React.useMemo(() => {
    const idForNameLookup = selectedCompetitor;
    if (idForNameLookup && competitors) {
      const found = competitors.find(c => c.id === idForNameLookup);
      if (found) return found.name;
      if (idForNameLookup) return idForNameLookup;
    }
    return "";
  }, [selectedCompetitor, competitors]);

  const {
    columns,
    rows,
    loading,
    error,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    onPageChange,
    onPageSizeChange,
  } = useProductMentionsTableData(selectedCompetitor, selectedPresetKey);

  const renderTableContent = () => {
    if (loading && rows.length === 0) { // Initial load
      return <StandardLoadingIndicator containerHeight="300px" />;
    }
    if (error) {
      return (
        <StandardMessageDisplay
          type="error"
          containerHeight="300px"
        />
      );
    }
    if (rows.length === 0 && !loading) {
      return (
        <StandardMessageDisplay
          type="noData"
          title="No Product Mentions Found"
          message={`No product mentions found for ${competitorDisplayName} in the selected timeframe.`}
          containerHeight="300px"
        />
      );
    }
    return <PorterTable columns={columns} rows={rows} />;
  };

  return (
    <GlassCard sx={{ position: "relative", overflow: "visible" }}>
      <VuiBox pb={0}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <VuiBox display="flex" alignItems="center">
            <VuiTypography variant="h6" color="white" fontWeight="medium">
              Product Mentions Activity
            </VuiTypography>
            <Tooltip
              title={
                <VuiTypography
                  variant="button"
                  color="white"
                  fontWeight="regular"
                  sx={{ fontSize: "11px", fontFamily: "Sora, sans-serif" }}
                >
                  Lists individual pieces of content (e.g., articles, posts) where the selected company\'s products are
                  mentioned, including source, date, and a snippet of the mention. How to use: Dive into specific
                  examples
                  of product mentions to understand context, and the nature of the discussion around your products or
                  competitor products.
                </VuiTypography>
              }
              placement="top"
              arrow
            >
              <div style={{ display: "inline-block", cursor: "pointer" }}>
                <VuiBox
                  component={IoInformationCircleOutline}
                  size="14px"
                  color="white"
                  ml={0.5}
                  sx={{ opacity: 0.7 }}
                />
              </div>
            </Tooltip>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" gap={1}>
            {/* Filters could go here in the future */}
            <IconButton
              onClick={handleOpenProductsTableModal}
              size="small"
              sx={{
                color: "#c8cfca",
                backgroundColor: "rgba(30, 30, 42, 0.7)",
                "&:hover": {
                  backgroundColor: "rgba(50, 50, 62, 0.9)",
                },
              }}
            >
              <AspectRatioIcon />
            </IconButton>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      <VuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {renderTableContent()}
        {rows.length > 0 && (
          <VuiPaginationControls
            currentPage={currentPage}
            totalPages={totalPages || 1}
            totalElements={totalElements || 0}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            isLoading={loading} // Show spinner on controls if loading more
          />
        )}
      </VuiBox>
      <Modal
        open={isProductsTableModalOpen}
        onClose={handleCloseProductsTableModal}
        aria-labelledby="products-table-modal-title"
      >
        <Paper sx={modalStyle}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography id="products-table-modal-title" variant="h6" color="white">
              News Articles
            </VuiTypography>
            <VuiBox>
              <IconButton onClick={handleCloseProductsTableModal} size="small">
                <CloseIcon sx={{ color: "#c8cfca" }} />
              </IconButton>
            </VuiBox>
          </VuiBox>
          <VuiBox sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {loading ? (
              <StandardLoadingIndicator />
            ) : error ? (
              <StandardMessageDisplay type="error" />
            ) : rows.length === 0 ? (
              <StandardMessageDisplay
                type="noData"
                title="No Product Mentions Found"
                message={`No product mentions found for ${competitorDisplayName} in the selected timeframe.`}
              />
            ) : (
              <PorterTable columns={columns} rows={rows} maxHeight="fill" />
            )}
          </VuiBox>
          {rows.length > 0 && (
            <VuiPaginationControls
              currentPage={currentPage}
              totalPages={totalPages || 1}
              totalElements={totalElements || 0}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              isLoading={loading}
            />
          )}
        </Paper>
      </Modal>
    </GlassCard>
  );
}

export default ProductMentionsTable; 